import {
  Flex,
  ReportSectionItemContent,
  ReportSectionItemTable,
  type ReportSectionItemTableProps,
  type ReportTableCell,
} from '@/components';
import { useLocaledAmount } from '@/providers';
import { formatFraction, formatValueByUnit } from '@/utils';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { LEDGER_BORDER } from '../../../utils';

export type DivergenceInExecutionItemBefore2025 = {
  fiscalYear: number | undefined;
  estimatedBusinessExpense: number | null | undefined;
  businessExpense: number | null | undefined;
  divergenceAmount: number | null | undefined;
  reason: string | null | undefined;
  divergenceRate: number | null | undefined;
};

export const KsFundDivergenceInExecutionItemBefore2025 = ({
  data,
}: {
  data: DivergenceInExecutionItemBefore2025[];
}) => {
  const { t } = useTranslation();
  const { unit } = useLocaledAmount();

  const businessExpensesTableData: ReportSectionItemTableProps = useMemo(() => {
    const header: ReportSectionItemTableProps['header'] = [
      {
        type: 'text',
        value: `${t('business-expenses')}${t('estimate')}`,
        ratio: 1,
        align: 'center',
      },
      {
        type: 'text',
        value: t('business-expenses'),
        ratio: 1,
        align: 'center',
      },
      {
        type: 'text',
        value: `${t('business-expenses')}${t('estimate')}`,
        ratio: 1,
        align: 'center',
      },
      {
        type: 'text',
        value: t('business-expenses'),
        ratio: 1,
        align: 'center',
      },
    ];

    const rowData: ReportSectionItemTableProps['data'] = [
      {
        row: [
          {
            type: 'text',
            value: formatValueByUnit(data[0]?.estimatedBusinessExpense, {
              operation: 'round',
              unit,
              showUnitLabel: false,
            }),
            align: 'right',
          },
          {
            type: 'text',
            value: formatValueByUnit(data[0]?.businessExpense, {
              operation: 'round',
              unit,
              showUnitLabel: false,
            }),
            align: 'right',
          },
          {
            type: 'text',
            value: formatValueByUnit(data[1]?.estimatedBusinessExpense, {
              operation: 'round',
              unit,
              showUnitLabel: false,
            }),
            align: 'right',
          },
          {
            type: 'text',
            value: formatValueByUnit(data[1]?.businessExpense, {
              operation: 'round',
              unit,
              showUnitLabel: false,
            }),
            align: 'right',
          },
        ] as ReportTableCell[],
      },
    ];

    return {
      header,
      data: rowData,
    };
  }, [data, t, unit]);

  const divergenceAmountTableData: ReportSectionItemTableProps = useMemo(() => {
    const header: ReportSectionItemTableProps['header'] = [
      {
        type: 'text',
        value: t('divergence-amount'),
        ratio: 1,
        align: 'center',
      },
      {
        type: 'text',
        value: t('divergence-rate'),
        ratio: 1,
        align: 'center',
      },
      {
        type: 'text',
        value: t('divergence-amount'),
        ratio: 1,
        align: 'center',
      },
      {
        type: 'text',
        value: t('divergence-rate'),
        ratio: 1,
        align: 'center',
      },
    ];

    const rowData: ReportSectionItemTableProps['data'] = [
      {
        row: [
          {
            type: 'text',
            value: formatValueByUnit(data[0]?.divergenceAmount, {
              operation: 'round',
              unit,
              showUnitLabel: false,
            }),
            align: 'right',
          },
          {
            type: 'text',
            value: formatFraction(data[0]?.divergenceRate, 100, {
              maximumFractionDigits: 1,
              useNegativeSign: true,
            }),
            align: 'right',
          },
          {
            type: 'text',
            value: formatValueByUnit(data[1]?.divergenceAmount, {
              operation: 'round',
              unit,
              showUnitLabel: false,
            }),
            align: 'right',
          },
          {
            type: 'text',
            value: formatFraction(data[1]?.divergenceRate, 100, {
              maximumFractionDigits: 1,
              useNegativeSign: true,
            }),
            align: 'right',
          },
        ] as ReportTableCell[],
      },
    ];

    return {
      header,
      data: rowData,
    };
  }, [data, t, unit]);

  return (
    <div>
      <ReportSectionItemContent
        isTable
        value={
          <ReportSectionItemTable
            header={[
              {
                type: 'text',
                value: `${data[0]?.fiscalYear?.toString()}（${t('fiscal-year-two-years-ago')}）`,
                ratio: 1,
                align: 'center',
              },
              {
                type: 'text',
                value: `${data[1]?.fiscalYear?.toString()}（${t('fiscal-year-one-year-ago')}）`,
                ratio: 1,
                align: 'center',
              },
            ]}
            data={[]}
          />
        }
        wrapperStyle={{ borderBottom: LEDGER_BORDER }}
      />

      <ReportSectionItemContent
        isTable
        value={
          <ReportSectionItemTable
            header={businessExpensesTableData.header}
            data={businessExpensesTableData.data}
          />
        }
        wrapperStyle={{ borderBottom: LEDGER_BORDER }}
      />

      <ReportSectionItemContent
        isTable
        value={
          <ReportSectionItemTable
            header={divergenceAmountTableData.header}
            data={divergenceAmountTableData.data}
          />
        }
        wrapperStyle={{ borderBottom: LEDGER_BORDER }}
      />

      <Flex>
        <ReportSectionItemContent
          isLast
          label={t('reason-for-divergence')}
          value={data[0]?.reason}
        />
        <ReportSectionItemContent
          isLast
          label={t('reason-for-divergence')}
          value={data[1]?.reason}
          wrapperStyle={{ borderLeft: LEDGER_BORDER }}
        />
      </Flex>
    </div>
  );
};
